/**
 * Login Commercial — "Sizzle Reel"
 * ─────────────────────────────────────────────────────────────
 * A cinematic, auto-playing 90-second commercial that walks new
 * visitors through every section of Yisrael Learning Center.
 *
 *  • 1 brand intro scene (animated SVG/CSS, swap-in Sora 2 MP4 later)
 *  • 9 feature scenes — each a custom branded illustration
 *  • Synchronized OpenAI-TTS voiceover (onyx) for every scene
 *  • Optional looping instrumental music bed
 *  • Skip / Mute / Replay controls, progress bar, scene chips
 *  • Plays once on first visit (localStorage), then hidden until
 *    user clicks "Watch Tour" pill
 *  • Mobile-first responsive, social-media-ready 16:9 frame
 */

import { useEffect, useRef, useState, useCallback } from "react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ASSETS = `${BACKEND}/api/static/commercial`;

// Each scene: voiceover file + caption + paint fn
const SCENES = [
  {
    id: 1,
    label: "Welcome",
    audio: null, // brand intro — uses ambient swell only
    durationMs: 6500,
    caption: "Yisrael Learning Center",
    sub: "Restoring Scriptural Knowledge for Every Nation",
    paint: "intro",
  },
  {
    id: 2,
    label: "Welcome",
    audio: "vo_scene01.mp3",
    durationMs: 9500,
    caption: "A Global Institution",
    sub: "Restoring the heritage of the Holy Scriptures",
    paint: "globe",
  },
  {
    id: 3,
    label: "K-12 Academy",
    audio: "vo_scene02.mp3",
    durationMs: 10500,
    caption: "K–12 Academic Program",
    sub: "Fully Accredited · 50-State Compliant",
    paint: "k12",
  },
  {
    id: 4,
    label: "Living School",
    audio: "vo_scene03.mp3",
    durationMs: 9500,
    caption: "The Living School",
    sub: "Synchronized with the Creator's Clock",
    paint: "living",
  },
  {
    id: 5,
    label: "Observatory",
    audio: "vo_scene04.mp3",
    durationMs: 12000,
    caption: "Celestial Observatory",
    sub: "364-Day Enochian Solar Cycle · 6 Gates of the Sun",
    paint: "observatory",
  },
  {
    id: 6,
    label: "AR Sky Map",
    audio: "vo_scene05.mp3",
    durationMs: 9500,
    caption: "Augmented-Reality Sky Map",
    sub: "Point your phone at the heavens",
    paint: "ar",
  },
  {
    id: 7,
    label: "Voice Teacher",
    audio: "vo_scene06.mp3",
    durationMs: 9500,
    caption: "The Invisible Teacher",
    sub: "Voice-first AI · answers anything · every grade",
    paint: "voice",
  },
  {
    id: 8,
    label: "Etymology",
    audio: "vo_scene07.mp3",
    durationMs: 9500,
    caption: "Forensic Etymology",
    sub: "Trace every word back to its Hebrew root",
    paint: "etymology",
  },
  {
    id: 9,
    label: "Certificates",
    audio: "vo_scene08.mp3",
    durationMs: 9500,
    caption: "Diplomas · Seminary · Ordination",
    sub: "Your testimony of mastery — preserved for life",
    paint: "certificate",
  },
  {
    id: 10,
    label: "Daily Rhythm",
    audio: "vo_scene09.mp3",
    durationMs: 8500,
    caption: "Your Daily Sky Brief",
    sub: "Sabbaths, feasts, and preparation days at a glance",
    paint: "skybrief",
  },
  {
    id: 11,
    label: "Begin",
    audio: "vo_scene10.mp3",
    durationMs: 11000,
    caption: "Begin Your Journey",
    sub: "7-Day Free Trial · $19/month · All Are Welcome",
    paint: "cta",
  },
];

const TOTAL_MS = SCENES.reduce((s, x) => s + x.durationMs, 0);

export default function LoginCommercial({ onClose, onBeginJourney }) {
  const [sceneIdx, setSceneIdx] = useState(0);
  const [elapsedMs, setElapsedMs] = useState(0);
  // Start MUTED — browsers (Chrome / Safari) block audio autoplay until
  // the user interacts with the page. Visuals autoplay; one tap unmutes.
  const [muted, setMuted] = useState(true);
  const [paused, setPaused] = useState(false);
  const [musicAvailable, setMusicAvailable] = useState(true);
  const audioRef = useRef(null);
  const musicRef = useRef(null);
  const tickRef = useRef(null);

  // Sequencer: advance scenes by elapsedMs
  useEffect(() => {
    if (paused) return;
    tickRef.current = setInterval(() => {
      setElapsedMs((prev) => {
        const next = prev + 100;
        if (next >= TOTAL_MS) {
          clearInterval(tickRef.current);
          return TOTAL_MS;
        }
        return next;
      });
    }, 100);
    return () => clearInterval(tickRef.current);
  }, [paused]);

  // Derive current scene from elapsedMs
  useEffect(() => {
    let acc = 0;
    for (let i = 0; i < SCENES.length; i++) {
      acc += SCENES[i].durationMs;
      if (elapsedMs < acc) {
        if (i !== sceneIdx) setSceneIdx(i);
        return;
      }
    }
  }, [elapsedMs, sceneIdx]);

  // Play scene voiceover when scene changes
  useEffect(() => {
    const scene = SCENES[sceneIdx];
    if (!scene || !scene.audio || muted) {
      if (audioRef.current) audioRef.current.pause();
      return;
    }
    if (audioRef.current) {
      audioRef.current.src = `${ASSETS}/${scene.audio}`;
      audioRef.current.currentTime = 0;
      audioRef.current.volume = 1.0;
      audioRef.current.play().catch(() => {});
    }
  }, [sceneIdx, muted]);

  // Music: try to load /static/commercial/music.mp3 — silent gracefully if missing
  useEffect(() => {
    if (!musicRef.current) return;
    const tag = musicRef.current;
    tag.volume = 0.15;
    tag.loop = true;
    const onErr = () => setMusicAvailable(false);
    tag.addEventListener("error", onErr);
    if (!muted && !paused) tag.play().catch(() => {});
    return () => tag.removeEventListener("error", onErr);
  }, [muted, paused]);

  // Mute toggle
  useEffect(() => {
    if (audioRef.current) audioRef.current.muted = muted;
    if (musicRef.current) musicRef.current.muted = muted;
  }, [muted]);

  // Pause toggle
  useEffect(() => {
    if (paused) {
      audioRef.current?.pause();
      musicRef.current?.pause();
    } else {
      audioRef.current?.play().catch(() => {});
      musicRef.current?.play().catch(() => {});
    }
  }, [paused]);

  const handleClose = useCallback(() => {
    localStorage.setItem("ylc_commercial_seen", "1");
    onClose && onClose();
  }, [onClose]);

  // ESC key to close
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleClose]);

  // Auto-close at end
  useEffect(() => {
    if (elapsedMs >= TOTAL_MS) {
      const t = setTimeout(handleClose, 1200);
      return () => clearTimeout(t);
    }
  }, [elapsedMs, handleClose]);

  const scene = SCENES[sceneIdx] || SCENES[0];
  const progress = Math.min(100, (elapsedMs / TOTAL_MS) * 100);

  return (
    <div data-testid="login-commercial" style={overlayStyle} onClick={handleClose}>
      {/* Always-on-top Skip button — fixed to the viewport corner so it
          cannot be occluded by chip overflow on narrow screens.
          Uses pointerdown so touch fires even if the parent overlay
          swallows a click. */}
      <button
        data-testid="commercial-skip-btn-fixed"
        aria-label="Skip tutorial"
        onClick={(e) => { e.stopPropagation(); handleClose(); }}
        onPointerDown={(e) => { e.stopPropagation(); handleClose(); }}
        style={fixedSkipBtnStyle}
      >
        Skip ✕
      </button>

      <div style={frameStyle} onClick={(e) => e.stopPropagation()}>
        {/* 16:9 canvas */}
        <div style={canvasStyle}>
          <SceneCanvas paint={scene.paint} caption={scene.caption} sub={scene.sub} />

          {/* "Tap for sound" hint — visible only while muted */}
          {muted && (
            <button
              data-testid="commercial-unmute-hint"
              onClick={() => setMuted(false)}
              style={tapForSoundStyle}
              title="Enable sound"
            >
              🔊 Tap for sound
            </button>
          )}

          {/* Top chrome: skip + chips */}
          <div style={topChrome}>
            <div style={chipsRow}>
              {SCENES.map((s, i) => (
                <div
                  key={s.id}
                  style={{
                    ...chipStyle,
                    background:
                      i === sceneIdx
                        ? "linear-gradient(90deg, #FFD700, #FFA500)"
                        : i < sceneIdx
                        ? "#FFD70080"
                        : "#ffffff20",
                    color: i === sceneIdx ? "#000" : "#ddd",
                  }}
                >
                  {s.label}
                </div>
              ))}
            </div>
            <button
              data-testid="commercial-skip-btn"
              onClick={(e) => { e.stopPropagation(); handleClose(); }}
              onPointerDown={(e) => { e.stopPropagation(); handleClose(); }}
              style={skipBtnStyle}
            >
              Skip ✕
            </button>
          </div>

          {/* Bottom chrome: controls + progress */}
          <div style={bottomChrome}>
            <div style={progressBar}>
              <div style={{ ...progressFill, width: `${progress}%` }} />
            </div>
            <div style={controlRow}>
              <button
                data-testid="commercial-pause-btn"
                onClick={() => setPaused((p) => !p)}
                style={ctrlBtnStyle}
                title={paused ? "Resume" : "Pause"}
              >
                {paused ? "▶︎" : "❚❚"}
              </button>
              <button
                data-testid="commercial-mute-btn"
                onClick={() => setMuted((m) => !m)}
                style={ctrlBtnStyle}
                title={muted ? "Unmute" : "Mute"}
              >
                {muted ? "🔇" : "🔊"}
              </button>
              <div style={{ flex: 1 }} />
              {sceneIdx === SCENES.length - 1 && (
                <button
                  data-testid="commercial-begin-btn"
                  onClick={() => {
                    handleClose();
                    onBeginJourney && onBeginJourney();
                  }}
                  style={ctaBtnStyle}
                >
                  Begin Free Trial →
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Hidden audio elements */}
      <audio ref={audioRef} preload="auto" />
      {musicAvailable && (
        <audio
          ref={musicRef}
          src={`${ASSETS}/music.mp3`}
          preload="auto"
          loop
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Scene paint registry — each is a self-contained branded SVG
 * illustration. Keep them production-grade, not cartoonish.
 * ───────────────────────────────────────────────────────────── */

function SceneCanvas({ paint, caption, sub }) {
  return (
    <div style={sceneBg}>
      <div style={sceneOrnaments}>
        <Paint name={paint} />
      </div>
      <div style={captionWrap}>
        <h2 style={captionTitle}>{caption}</h2>
        <p style={captionSub}>{sub}</p>
      </div>
      <div style={vignette} />
    </div>
  );
}

function Paint({ name }) {
  switch (name) {
    case "intro":
      return <IntroPaint />;
    case "globe":
      return <GlobePaint />;
    case "k12":
      return <K12Paint />;
    case "living":
      return <LivingPaint />;
    case "observatory":
      return <ObservatoryPaint />;
    case "ar":
      return <ARPaint />;
    case "voice":
      return <VoicePaint />;
    case "etymology":
      return <EtymologyPaint />;
    case "certificate":
      return <CertificatePaint />;
    case "skybrief":
      return <SkyBriefPaint />;
    case "cta":
      return <CTAPaint />;
    default:
      return null;
  }
}

/* ─── Brand intro: full cinematic open (Sora-grade, 100% free CSS/SVG) ── */
function IntroPaint() {
  // 80 deterministic stars across the upper sky band
  const stars = Array.from({ length: 80 }).map((_, i) => ({
    x: (i * 137 + 31) % 1280,
    y: (i * 89 + 17) % 420,
    r: (i % 4) * 0.4 + 0.5,
    delay: (i % 11) * 0.3,
  }));
  // 18 dust particles drifting upward through the light beam
  const dust = Array.from({ length: 18 }).map((_, i) => ({
    x: 580 + ((i * 53) % 120),
    y: 700 - (i * 7) % 280,
    r: (i % 3) * 0.5 + 0.8,
    delay: (i % 9) * 0.5,
    dur: 6 + (i % 5),
  }));
  // 14 light rays radiating from the sun
  const rays = Array.from({ length: 14 }).map((_, i) => ({
    angle: (i * 360) / 14,
    delay: i * 0.18,
  }));

  return (
    <svg viewBox="0 0 1280 720" style={svgFull} preserveAspectRatio="xMidYMid slice">
      <defs>
        <radialGradient id="introBg" cx="50%" cy="65%" r="80%">
          <stop offset="0%" stopColor="#221833" />
          <stop offset="50%" stopColor="#0a0a1a" />
          <stop offset="100%" stopColor="#000" />
        </radialGradient>
        <radialGradient id="sunCore" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#fff8e0" stopOpacity="1" />
          <stop offset="40%" stopColor="#FFD700" stopOpacity="0.95" />
          <stop offset="100%" stopColor="#FFA500" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="sunGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#FFD700" stopOpacity="0.55" />
          <stop offset="60%" stopColor="#FFA500" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#FFA500" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="rayGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFD700" stopOpacity="0.65" />
          <stop offset="100%" stopColor="#FFD700" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="horizonGlow" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFA500" stopOpacity="0" />
          <stop offset="100%" stopColor="#FF6a1a" stopOpacity="0.35" />
        </linearGradient>
        <filter id="softGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="4" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <filter id="bigGlow" x="-100%" y="-100%" width="300%" height="300%">
          <feGaussianBlur stdDeviation="12" />
        </filter>
      </defs>

      {/* Background gradient sky */}
      <rect width="1280" height="720" fill="url(#introBg)" />

      {/* Starfield with staggered twinkle */}
      <g className="ylc-stars">
        {stars.map((s, i) => (
          <circle
            key={i}
            cx={s.x}
            cy={s.y}
            r={s.r}
            fill="#ffffff"
            style={{
              animation: `ylcTwinkle 2.6s ease-in-out ${s.delay}s infinite`,
              transformOrigin: `${s.x}px ${s.y}px`,
            }}
          />
        ))}
      </g>

      {/* Horizon ember-glow (deep warm at the bottom) */}
      <rect x="0" y="500" width="1280" height="220" fill="url(#horizonGlow)" />

      {/* Sun ray fan (14 rays sweeping out, then steady) */}
      <g transform="translate(640, 480)" style={{ filter: "url(#bigGlow)" }}>
        {rays.map((r, i) => (
          <rect
            key={i}
            x="-3"
            y="-560"
            width="6"
            height="560"
            fill="url(#rayGrad)"
            opacity="0"
            style={{
              "--a": `${r.angle}deg`,
              transform: `rotate(${r.angle}deg)`,
              transformOrigin: "0 0",
              animation: `ylcRayBurst 4.5s ease-out ${r.delay}s forwards`,
            }}
          />
        ))}
      </g>

      {/* Sun outer halo */}
      <circle
        cx="640"
        cy="480"
        r="320"
        fill="url(#sunGlow)"
        style={{ animation: "ylcSunBreath 5s ease-in-out infinite alternate" }}
      />

      {/* Sun core (rises from below the frame) */}
      <g style={{ animation: "ylcSunRise 3.5s cubic-bezier(.22,.61,.36,1) forwards" }}>
        <circle cx="640" cy="480" r="120" fill="url(#sunCore)" />
        <circle cx="640" cy="480" r="60" fill="#fff8e0" opacity="0.85" />
      </g>

      {/* Drifting dust particles inside the light column */}
      <g>
        {dust.map((d, i) => (
          <circle
            key={i}
            cx={d.x}
            cy={d.y}
            r={d.r}
            fill="#FFD700"
            opacity="0.55"
            style={{
              animation: `ylcDustRise ${d.dur}s linear ${d.delay}s infinite`,
            }}
          />
        ))}
      </g>

      {/* Menorah — flames light up sequentially after the sun has risen */}
      <g transform="translate(640, 600)" stroke="#FFD700" strokeWidth="3" fill="none" style={{ filter: "url(#softGlow)" }}>
        <line x1="0" y1="0" x2="0" y2="-130" />
        <line x1="-170" y1="0" x2="170" y2="0" strokeWidth="4" />
        {[
          { x: -150, h: 92 },
          { x: -100, h: 80 },
          { x: -50, h: 70 },
          { x: 0, h: 130, center: true },
          { x: 50, h: 70 },
          { x: 100, h: 80 },
          { x: 150, h: 92 },
        ].map((arm, i) => (
          <g key={i}>
            {!arm.center && (
              <path d={`M ${arm.x} 0 Q ${arm.x} ${-arm.h} 0 ${-arm.h}`} />
            )}
            <line x1={arm.x} y1={arm.center ? -130 : -arm.h} x2={arm.x} y2={(arm.center ? -130 : -arm.h) - 22} />
            {/* Flame */}
            <ellipse
              cx={arm.x}
              cy={(arm.center ? -130 : -arm.h) - 30}
              rx="4"
              ry="9"
              fill="#FFD700"
              stroke="none"
              opacity="0"
              style={{
                animation: `ylcFlameLight 0.7s ease-out ${1.8 + i * 0.18}s forwards`,
                transformOrigin: `${arm.x}px ${(arm.center ? -130 : -arm.h) - 30}px`,
              }}
            />
            <ellipse
              cx={arm.x}
              cy={(arm.center ? -130 : -arm.h) - 33}
              rx="2"
              ry="5"
              fill="#fff8e0"
              stroke="none"
              opacity="0"
              style={{
                animation: `ylcFlameLight 0.7s ease-out ${1.95 + i * 0.18}s forwards`,
              }}
            />
          </g>
        ))}
        {/* Base */}
        <rect x="-44" y="0" width="88" height="14" fill="#FFD700" opacity="0.4" stroke="none" />
        <rect x="-20" y="14" width="40" height="6" fill="#FFD700" opacity="0.6" stroke="none" />
      </g>

      {/* Lens flare orbs (subtle, drifting) */}
      <g style={{ mixBlendMode: "screen" }}>
        <circle cx="320" cy="220" r="40" fill="#FFD700" opacity="0.12" style={{ animation: "ylcFlareDrift 9s ease-in-out infinite alternate" }} />
        <circle cx="980" cy="300" r="55" fill="#FFA500" opacity="0.10" style={{ animation: "ylcFlareDrift 11s ease-in-out 1.5s infinite alternate-reverse" }} />
        <circle cx="200" cy="600" r="80" fill="#FFA500" opacity="0.08" style={{ animation: "ylcFlareDrift 13s ease-in-out 0.5s infinite alternate" }} />
      </g>
    </svg>
  );
}

/* ─── Globe with longitude rays ── */
function GlobePaint() {
  return (
    <svg viewBox="0 0 1280 720" style={svgFull}>
      <defs>
        <radialGradient id="globeGrad" cx="40%" cy="40%" r="60%">
          <stop offset="0%" stopColor="#4a6fbf" />
          <stop offset="100%" stopColor="#0a1a3a" />
        </radialGradient>
      </defs>
      <rect width="1280" height="720" fill="#050518" />
      <circle cx="640" cy="380" r="260" fill="url(#globeGrad)" stroke="#FFD70080" strokeWidth="1.5" />
      {/* Slow rotation of meridians */}
      <g transform="translate(640, 380)" style={{ animation: "ylcRotateSlow 50s linear infinite" }}>
        {[0, 30, 60, 90, 120, 150].map((a) => (
          <ellipse
            key={a}
            cx="0"
            cy="0"
            rx="260"
            ry={Math.max(60, 260 - Math.abs(90 - a) * 2.4)}
            fill="none"
            stroke="#FFD70030"
            strokeWidth="1"
            transform={`rotate(${a})`}
          />
        ))}
      </g>
      {/* Ray fan from center */}
      {[0, 60, 120, 180, 240, 300].map((a, i) => {
        const rad = (a * Math.PI) / 180;
        return (
          <line
            key={a}
            x1={640 + 260 * Math.cos(rad)}
            y1={380 + 260 * Math.sin(rad)}
            x2={640 + 420 * Math.cos(rad)}
            y2={380 + 420 * Math.sin(rad)}
            stroke="#FFD70060"
            strokeWidth="1"
            style={{
              animation: `ylcRayPulse 3s ease-in-out ${i * 0.4}s infinite alternate`,
            }}
          />
        );
      })}
      <circle cx="640" cy="380" r="6" fill="#FFD700" style={{ animation: "ylcMoonPulse 2.6s ease-in-out infinite" }} />
    </svg>
  );
}

/* ─── K-12 grade grid ── */
function K12Paint() {
  return (
    <svg viewBox="0 0 1280 720" style={svgFull}>
      <rect width="1280" height="720" fill="#0a0a1a" />
      <g transform="translate(180, 180)">
        {["K", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"].map((g, i) => {
          const col = i % 5;
          const row = Math.floor(i / 5);
          return (
            <g key={g} transform={`translate(${col * 190}, ${row * 130})`}>
              <rect
                width="170"
                height="110"
                rx="12"
                fill="#1a1a2e"
                stroke="#FFD700"
                strokeWidth={i === 6 ? 3 : 1}
                opacity={i === 6 ? 1 : 0.8}
              />
              <text
                x="85"
                y="55"
                textAnchor="middle"
                fontSize="38"
                fill="#FFD700"
                fontFamily="Georgia, serif"
                fontWeight="bold"
              >
                {g}
              </text>
              <text x="85" y="82" textAnchor="middle" fontSize="11" fill="#aaa">
                Grade
              </text>
            </g>
          );
        })}
      </g>
    </svg>
  );
}

/* ─── Living School: lunar cycle wheel ── */
function LivingPaint() {
  return (
    <svg viewBox="0 0 1280 720" style={svgFull}>
      <rect width="1280" height="720" fill="#0a0a1a" />
      {/* Slow rotation of the entire wheel */}
      <g transform="translate(640, 380)" style={{ animation: "ylcRotateSlow 60s linear infinite" }}>
        {Array.from({ length: 30 }).map((_, i) => {
          const a = (i / 30) * Math.PI * 2 - Math.PI / 2;
          const r = 240;
          const x = r * Math.cos(a);
          const y = r * Math.sin(a);
          const active = i === 13;
          return (
            <g key={i} style={active ? { animation: "ylcMoonPulse 1.4s ease-in-out infinite", transformOrigin: `${x}px ${y}px` } : null}>
              <circle cx={x} cy={y} r={active ? 22 : 10} fill={active ? "#FFD700" : "#1a1a3a"} stroke="#FFD70080" strokeWidth="1" />
              <text x={x} y={y + 4} textAnchor="middle" fontSize={active ? 16 : 10} fill={active ? "#000" : "#888"} fontWeight={active ? "bold" : "normal"}>
                {i + 1}
              </text>
            </g>
          );
        })}
      </g>
      {/* Center plate (static — doesn't rotate with wheel) */}
      <circle cx="640" cy="380" r="80" fill="#0a0a1a" stroke="#FFD70060" strokeWidth="1" />
      <text x="640" y="372" textAnchor="middle" fontSize="20" fill="#FFD700" fontFamily="Georgia, serif">Day 14</text>
      <text x="640" y="398" textAnchor="middle" fontSize="13" fill="#aaa">of Ziv</text>
    </svg>
  );
}

/* ─── Observatory: sun + 6 gates ── */
function ObservatoryPaint() {
  return (
    <svg viewBox="0 0 1280 720" style={svgFull}>
      <defs>
        <radialGradient id="obsCore" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#fff8e0" />
          <stop offset="100%" stopColor="#FFA500" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="1280" height="720" fill="#05050f" />
      {/* Slow-rotating ring of orbits */}
      <g transform="translate(640, 380)" style={{ animation: "ylcRotateSlow 30s linear infinite" }}>
        <circle r="290" fill="none" stroke="#FFD70025" strokeWidth="1" strokeDasharray="2 6" />
        <circle r="230" fill="none" stroke="#FFD70030" strokeWidth="1" />
        <circle r="160" fill="none" stroke="#FFD70040" strokeWidth="1" strokeDasharray="4 6" />
      </g>
      {/* Sun core with breath */}
      <g style={{ animation: "ylcSunBreath 4s ease-in-out infinite alternate" }}>
        <circle cx="640" cy="380" r="160" fill="url(#obsCore)" />
        <circle cx="640" cy="380" r="60" fill="#FFD700" />
      </g>
      {/* Six gates light up one by one */}
      {[1, 2, 3, 4, 5, 6].map((g) => {
        const a = ((g - 1) * 60 - 90) * (Math.PI / 180);
        const r = 230;
        const x = 640 + r * Math.cos(a);
        const y = 380 + r * Math.sin(a);
        const active = g === 5;
        return (
          <g
            key={g}
            style={{
              opacity: 0,
              animation: `ylcGatePop 0.5s ease-out ${0.4 + g * 0.18}s forwards`,
              transformOrigin: `${x}px ${y}px`,
            }}
          >
            <line x1="640" y1="380" x2={x} y2={y} stroke={active ? "#FFD700" : "#FFD70040"} strokeWidth={active ? 2 : 1} />
            <circle cx={x} cy={y} r={active ? 38 : 28} fill={active ? "#FFD700" : "#1a1a2e"} stroke="#FFD700" strokeWidth="2" />
            <text x={x} y={y + 7} textAnchor="middle" fontSize="22" fill={active ? "#000" : "#FFD700"} fontWeight="bold">{g}</text>
          </g>
        );
      })}
      <text x="640" y="680" textAnchor="middle" fontSize="18" fill="#aaa">Gate 5 · Late Spring · Approaching Solstice</text>
    </svg>
  );
}

/* ─── AR: phone overlay ── */
function ARPaint() {
  return (
    <svg viewBox="0 0 1280 720" style={svgFull}>
      <defs>
        <linearGradient id="skyAR" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1a2050" />
          <stop offset="100%" stopColor="#4a3a30" />
        </linearGradient>
      </defs>
      <rect width="1280" height="720" fill="url(#skyAR)" />
      {Array.from({ length: 40 }).map((_, i) => {
        const x = (i * 173) % 1280;
        const y = (i * 91) % 720;
        return <circle key={i} cx={x} cy={y} r="1.2" fill="#fff" opacity="0.6" />;
      })}
      {/* Phone frame */}
      <g transform="translate(440, 90)">
        <rect width="400" height="560" rx="40" fill="#0a0a0a" stroke="#FFD700" strokeWidth="3" />
        <rect x="20" y="60" width="360" height="450" rx="8" fill="#0a1530" />
        {/* Crosshair */}
        <circle cx="200" cy="285" r="80" fill="none" stroke="#FFD700" strokeWidth="2" strokeDasharray="4 4" />
        <line x1="200" y1="200" x2="200" y2="370" stroke="#FFD70060" />
        <line x1="115" y1="285" x2="285" y2="285" stroke="#FFD70060" />
        {/* Sun + moon markers */}
        <circle cx="240" cy="240" r="14" fill="#FFD700" />
        <text x="240" y="222" textAnchor="middle" fontSize="11" fill="#FFD700">SUN</text>
        <circle cx="155" cy="320" r="12" fill="#cce" />
        <text x="155" y="345" textAnchor="middle" fontSize="11" fill="#cce">MOON</text>
        <rect x="20" y="520" width="360" height="20" rx="3" fill="#1a1a2e" />
        <text x="200" y="535" textAnchor="middle" fontSize="11" fill="#FFD700">Heading 178° · Calibrated</text>
      </g>
    </svg>
  );
}

/* ─── Voice teacher: waveform + question bubble ── */
function VoicePaint() {
  const bars = Array.from({ length: 40 }).map((_, i) => ({
    i,
    base: 30 + Math.abs(Math.sin(i * 0.6)) * 70 + (i % 5) * 6,
    delay: (i % 7) * 0.08,
  }));
  return (
    <svg viewBox="0 0 1280 720" style={svgFull}>
      <rect width="1280" height="720" fill="#0a0a1a" />
      {/* Speech bubble */}
      <g transform="translate(180, 180)" style={{ animation: "ylcSlideInLeft 0.8s ease-out" }}>
        <rect width="920" height="120" rx="60" fill="#1a1a2e" stroke="#FFD700" strokeWidth="2" />
        <text x="460" y="68" textAnchor="middle" fontSize="22" fill="#FFD700" fontFamily="Georgia, serif">"What is the meaning of the 14 parts of light?"</text>
        <text x="460" y="98" textAnchor="middle" fontSize="14" fill="#888">— Student question</text>
      </g>
      {/* Live, dancing waveform */}
      <g transform="translate(640, 440)">
        {bars.map((b) => (
          <rect
            key={b.i}
            x={(b.i - 20) * 18}
            y={-b.base / 2}
            width="8"
            height={b.base}
            rx="4"
            fill="#FFD700"
            opacity={0.5 + (b.i % 3) * 0.2}
            style={{
              animation: `ylcWavebar 0.9s ease-in-out ${b.delay}s infinite alternate`,
              transformOrigin: `${(b.i - 20) * 18 + 4}px 0px`,
            }}
          />
        ))}
      </g>
      <text x="640" y="620" textAnchor="middle" fontSize="18" fill="#aaa">The Invisible Teacher is listening…</text>
    </svg>
  );
}

/* ─── Forensic Etymology: Hebrew → English ── */
function EtymologyPaint() {
  return (
    <svg viewBox="0 0 1280 720" style={svgFull}>
      <rect width="1280" height="720" fill="#0a0a1a" />
      <g transform="translate(640, 380)">
        <text x="-360" y="0" textAnchor="middle" fontSize="120" fill="#FFD700" fontFamily="Georgia, serif">דבר</text>
        <text x="-360" y="60" textAnchor="middle" fontSize="18" fill="#aaa">davar</text>
        <text x="-360" y="90" textAnchor="middle" fontSize="14" fill="#888">root · Hebrew</text>
        <path d="M -200 0 L 200 0" stroke="#FFD700" strokeWidth="3" />
        <polygon points="200,-15 230,0 200,15" fill="#FFD700" />
        <text x="0" y="-30" textAnchor="middle" fontSize="14" fill="#FFD700">restored meaning</text>
        <text x="360" y="-10" textAnchor="middle" fontSize="48" fill="#fff" fontFamily="Georgia, serif">Word · Thing</text>
        <text x="360" y="30" textAnchor="middle" fontSize="48" fill="#fff" fontFamily="Georgia, serif">Matter · Promise</text>
        <text x="360" y="80" textAnchor="middle" fontSize="14" fill="#888">English derivatives</text>
      </g>
    </svg>
  );
}

/* ─── Certificate / Diploma ── */
function CertificatePaint() {
  return (
    <svg viewBox="0 0 1280 720" style={svgFull}>
      <rect width="1280" height="720" fill="#0a0a1a" />
      <g transform="translate(640, 380)">
        <rect x="-320" y="-200" width="640" height="400" rx="6" fill="#f4ecd6" stroke="#8B4513" strokeWidth="4" />
        <rect x="-300" y="-180" width="600" height="360" fill="none" stroke="#8B4513" strokeWidth="1" />
        <text x="0" y="-120" textAnchor="middle" fontSize="20" fill="#8B4513" letterSpacing="6">CERTIFICATE OF MASTERY</text>
        <line x1="-220" y1="-90" x2="220" y2="-90" stroke="#8B4513" />
        <text x="0" y="-40" textAnchor="middle" fontSize="14" fill="#5a3a1a">this certifies that</text>
        <text x="0" y="10" textAnchor="middle" fontSize="32" fill="#1a1a1a" fontFamily="Georgia, serif" fontStyle="italic">Student Name</text>
        <text x="0" y="50" textAnchor="middle" fontSize="14" fill="#5a3a1a">has completed</text>
        <text x="0" y="80" textAnchor="middle" fontSize="20" fill="#1a1a1a" fontFamily="Georgia, serif">Seminary · Ordination Track</text>
        <circle cx="0" cy="140" r="34" fill="none" stroke="#8B4513" strokeWidth="2" />
        <text x="0" y="147" textAnchor="middle" fontSize="14" fill="#8B4513" fontWeight="bold">YLC</text>
      </g>
    </svg>
  );
}

/* ─── Sky Brief banner ── */
function SkyBriefPaint() {
  return (
    <svg viewBox="0 0 1280 720" style={svgFull}>
      <rect width="1280" height="720" fill="#0a0a1a" />
      <g transform="translate(140, 280)">
        <rect width="1000" height="120" rx="14" fill="#1a1a2e" stroke="#FFD700" strokeWidth="1.5" />
        <rect width="6" height="120" rx="3" fill="#CD853F" />
        <text x="60" y="50" fontSize="36">🌕</text>
        <text x="120" y="55" fontSize="22" fill="#FFD700" fontFamily="Georgia, serif">Day 14 of Ziv</text>
        <text x="330" y="55" fontSize="22" fill="#CD853F" fontFamily="Georgia, serif">· Preparation Day</text>
        <text x="610" y="55" fontSize="22" fill="#87CEEB" fontFamily="Georgia, serif">· Full Moon Witness</text>
        <text x="60" y="90" fontSize="16" fill="#aaa">Sundown 8:18 PM · 1 day to High Sabbath</text>
        <text x="940" y="90" textAnchor="end" fontSize="14" fill="#888">Open Observatory →</text>
      </g>
    </svg>
  );
}

/* ─── CTA scene ── */
function CTAPaint() {
  return (
    <svg viewBox="0 0 1280 720" style={svgFull}>
      <defs>
        <radialGradient id="ctaGlow" cx="50%" cy="50%" r="60%">
          <stop offset="0%" stopColor="#FFD70060" />
          <stop offset="100%" stopColor="#0a0a1a" />
        </radialGradient>
      </defs>
      <rect width="1280" height="720" fill="url(#ctaGlow)" />
      {/* Floating sparkles */}
      {Array.from({ length: 30 }).map((_, i) => {
        const x = (i * 173 + 91) % 1280;
        const y = (i * 97 + 41) % 720;
        return (
          <circle
            key={i}
            cx={x}
            cy={y}
            r={(i % 3) + 1}
            fill="#FFD700"
            opacity="0.5"
            style={{
              animation: `ylcSparkle 3s ease-in-out ${(i % 11) * 0.25}s infinite`,
              transformOrigin: `${x}px ${y}px`,
            }}
          />
        );
      })}
      <text x="640" y="300" textAnchor="middle" fontSize="80" fill="#FFD700" fontFamily="Georgia, serif" fontWeight="bold" style={{ animation: "ylcSlideUp 0.8s ease-out" }}>7-Day Free Trial</text>
      <text x="640" y="380" textAnchor="middle" fontSize="36" fill="#fff" fontFamily="Georgia, serif" style={{ animation: "ylcSlideUp 0.8s ease-out 0.2s both" }}>Then $19/month</text>
      <text x="640" y="450" textAnchor="middle" fontSize="20" fill="#aaa" style={{ animation: "ylcSlideUp 0.8s ease-out 0.4s both" }}>All Are Welcome · Begin Your Journey Today</text>
      <g style={{ animation: "ylcMoonPulse 1.6s ease-in-out 0.6s infinite", transformOrigin: "640px 555px" }}>
        <rect x="490" y="520" width="300" height="70" rx="35" fill="#FFD700" />
        <text x="640" y="566" textAnchor="middle" fontSize="24" fill="#000" fontWeight="bold">Begin Free Trial →</text>
      </g>
    </svg>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Styles (inline so the component is self-contained)
 * ───────────────────────────────────────────────────────────── */

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.92)",
  zIndex: 9999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "20px",
  animation: "ylcFadeIn 0.4s ease",
};

const frameStyle = {
  width: "100%",
  maxWidth: "1100px",
  position: "relative",
};

const canvasStyle = {
  position: "relative",
  width: "100%",
  paddingBottom: "56.25%", // 16:9
  background: "#000",
  borderRadius: "16px",
  overflow: "hidden",
  boxShadow: "0 30px 100px rgba(255, 215, 0, 0.15), 0 0 0 1px #FFD70030",
};

const sceneBg = {
  position: "absolute",
  inset: 0,
  background: "#0a0a1a",
};

const sceneOrnaments = {
  position: "absolute",
  inset: 0,
  animation: "ylcKenBurns 12s ease-in-out infinite alternate",
};

const svgFull = { width: "100%", height: "100%", display: "block" };

const vignette = {
  position: "absolute",
  inset: 0,
  background:
    "radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.6) 100%)",
  pointerEvents: "none",
};

const captionWrap = {
  position: "absolute",
  bottom: "12%",
  left: "6%",
  right: "6%",
  textAlign: "left",
  pointerEvents: "none",
  animation: "ylcSlideUp 0.7s ease",
};

const captionTitle = {
  margin: 0,
  fontSize: "clamp(1.6rem, 4vw, 3rem)",
  color: "#FFD700",
  fontFamily: "Georgia, serif",
  fontWeight: 700,
  textShadow: "0 4px 24px rgba(0,0,0,0.7)",
  letterSpacing: "0.5px",
};

const captionSub = {
  margin: "8px 0 0",
  fontSize: "clamp(0.85rem, 1.5vw, 1.15rem)",
  color: "#eee",
  fontFamily: "Georgia, serif",
  textShadow: "0 2px 12px rgba(0,0,0,0.7)",
  letterSpacing: "0.3px",
};

const topChrome = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  padding: "14px 18px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  background:
    "linear-gradient(180deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)",
};

const chipsRow = {
  display: "flex",
  gap: "6px",
  flexWrap: "wrap",
  flex: 1,
  overflow: "hidden",
};

const chipStyle = {
  fontSize: "0.7rem",
  padding: "4px 9px",
  borderRadius: "999px",
  fontFamily: "Georgia, serif",
  whiteSpace: "nowrap",
  letterSpacing: "0.3px",
  transition: "all 0.4s ease",
};

const skipBtnStyle = {
  background: "rgba(255,255,255,0.12)",
  color: "#fff",
  border: "1px solid #ffffff30",
  borderRadius: "999px",
  padding: "8px 16px",
  fontSize: "0.85rem",
  cursor: "pointer",
  fontFamily: "Georgia, serif",
  touchAction: "manipulation",
  WebkitTapHighlightColor: "rgba(255,215,0,0.3)",
  minHeight: "40px",
  minWidth: "72px",
  flexShrink: 0,
};

const fixedSkipBtnStyle = {
  position: "fixed",
  top: "max(14px, env(safe-area-inset-top, 14px))",
  right: "max(14px, env(safe-area-inset-right, 14px))",
  zIndex: 10001,
  background: "rgba(0,0,0,0.75)",
  color: "#FFD700",
  border: "1.5px solid #FFD700",
  borderRadius: "999px",
  padding: "10px 18px",
  fontSize: "0.9rem",
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "Georgia, serif",
  touchAction: "manipulation",
  WebkitTapHighlightColor: "rgba(255,215,0,0.4)",
  minHeight: "44px",
  minWidth: "88px",
  boxShadow: "0 4px 14px rgba(0,0,0,0.6)",
};

const bottomChrome = {
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  padding: "12px 18px 16px",
  background:
    "linear-gradient(0deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0) 100%)",
};

const progressBar = {
  height: "3px",
  background: "#ffffff15",
  borderRadius: "2px",
  overflow: "hidden",
  marginBottom: "10px",
};

const progressFill = {
  height: "100%",
  background: "linear-gradient(90deg, #FFD700, #FFA500)",
  transition: "width 0.1s linear",
};

const controlRow = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
};

const ctrlBtnStyle = {
  background: "rgba(255,255,255,0.1)",
  color: "#fff",
  border: "1px solid #ffffff30",
  borderRadius: "999px",
  padding: "6px 12px",
  fontSize: "0.85rem",
  cursor: "pointer",
  minWidth: "44px",
};

const ctaBtnStyle = {
  background: "linear-gradient(90deg, #FFD700, #FFA500)",
  color: "#000",
  border: "none",
  borderRadius: "999px",
  padding: "10px 22px",
  fontSize: "0.95rem",
  fontWeight: "bold",
  cursor: "pointer",
  fontFamily: "Georgia, serif",
  boxShadow: "0 6px 24px rgba(255,215,0,0.5)",
  animation: "ylcPulse 1.6s ease-in-out infinite",
};

const tapForSoundStyle = {
  position: "absolute",
  top: "60px",
  right: "18px",
  zIndex: 20,
  background: "linear-gradient(90deg, #FFD700, #FFA500)",
  color: "#000",
  border: "none",
  borderRadius: "999px",
  padding: "10px 18px",
  fontSize: "0.85rem",
  fontWeight: "bold",
  fontFamily: "Georgia, serif",
  cursor: "pointer",
  boxShadow: "0 6px 24px rgba(255,215,0,0.5)",
  animation: "ylcPulse 1.4s ease-in-out infinite",
};

// inject keyframes once
if (typeof document !== "undefined" && !document.getElementById("ylc-commercial-css")) {
  const s = document.createElement("style");
  s.id = "ylc-commercial-css";
  s.innerHTML = `
    @keyframes ylcFadeIn { from { opacity: 0 } to { opacity: 1 } }
    @keyframes ylcSlideUp { from { transform: translateY(20px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
    @keyframes ylcSlideInLeft { from { transform: translateX(-30px); opacity: 0 } to { transform: translateX(0); opacity: 1 } }
    @keyframes ylcKenBurns {
      0% { transform: scale(1) translate(0,0); }
      100% { transform: scale(1.08) translate(-1.5%, -1%); }
    }
    @keyframes ylcPulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.04); }
    }
    /* ── Sora-grade cinematic motion primitives ── */
    @keyframes ylcTwinkle {
      0%, 100% { opacity: 0.25; transform: scale(0.6); }
      50%      { opacity: 1;    transform: scale(1.2); }
    }
    @keyframes ylcSunRise {
      0%   { transform: translateY(380px) scale(0.55); opacity: 0; }
      40%  { opacity: 1; }
      100% { transform: translateY(0)     scale(1);    opacity: 1; }
    }
    @keyframes ylcSunBreath {
      0%   { transform: scale(1);    opacity: 0.85; }
      100% { transform: scale(1.05); opacity: 1;    }
    }
    @keyframes ylcRayBurst {
      0%   { opacity: 0; transform: rotate(var(--a, 0deg)) scaleY(0.2); }
      60%  { opacity: 0.9; }
      100% { opacity: 0.55; transform: rotate(var(--a, 0deg)) scaleY(1); }
    }
    @keyframes ylcDustRise {
      0%   { transform: translate(0, 0);       opacity: 0; }
      15%  { opacity: 0.7; }
      85%  { opacity: 0.7; }
      100% { transform: translate(-12px, -300px); opacity: 0; }
    }
    @keyframes ylcFlameLight {
      0%   { opacity: 0; transform: scale(0.4) translateY(8px); }
      60%  { opacity: 1; transform: scale(1.2) translateY(0); }
      100% { opacity: 0.95; transform: scale(1) translateY(0); }
    }
    @keyframes ylcFlareDrift {
      0%   { transform: translate(0, 0) scale(1);    opacity: 0.6; }
      100% { transform: translate(40px, -20px) scale(1.15); opacity: 0.9; }
    }
    @keyframes ylcRotateSlow {
      from { transform: rotate(0deg); }
      to   { transform: rotate(360deg); }
    }
    @keyframes ylcMoonPulse {
      0%, 100% { transform: scale(1);    filter: brightness(1); }
      50%      { transform: scale(1.18); filter: brightness(1.3); }
    }
    @keyframes ylcGatePop {
      0%   { opacity: 0; transform: scale(0.3); }
      60%  { opacity: 1; transform: scale(1.18); }
      100% { opacity: 1; transform: scale(1); }
    }
    @keyframes ylcWavebar {
      0%, 100% { transform: scaleY(0.55); }
      50%      { transform: scaleY(1.3); }
    }
    @keyframes ylcRayPulse {
      0%, 100% { opacity: 0.3; }
      50%      { opacity: 1;   }
    }
    @keyframes ylcSparkle {
      0%, 100% { opacity: 0.1; transform: scale(0.6); }
      50%      { opacity: 1;   transform: scale(1.4); }
    }
    /* Scope: override app-wide button width:100% rule inside the commercial */
    [data-testid="login-commercial"] button {
      width: auto !important;
      margin-top: 0 !important;
      padding: 6px 14px !important;
      background: rgba(255,255,255,0.1) !important;
      color: #fff !important;
      border: 1px solid rgba(255,255,255,0.25) !important;
      border-radius: 999px !important;
      font-family: Georgia, serif !important;
      font-size: 0.85rem !important;
      cursor: pointer !important;
      box-shadow: none !important;
    }
    [data-testid="login-commercial"] button:hover {
      background: rgba(255,255,255,0.18) !important;
      transform: none !important;
    }
    [data-testid="login-commercial"] button[data-testid="commercial-begin-btn"] {
      background: linear-gradient(90deg, #FFD700, #FFA500) !important;
      color: #000 !important;
      border: none !important;
      padding: 10px 22px !important;
      font-weight: bold !important;
      box-shadow: 0 6px 24px rgba(255,215,0,0.5) !important;
      animation: ylcPulse 1.6s ease-in-out infinite;
    }
    [data-testid="login-commercial"] button[data-testid="commercial-unmute-hint"] {
      background: linear-gradient(90deg, #FFD700, #FFA500) !important;
      color: #000 !important;
      border: none !important;
      padding: 10px 18px !important;
      font-size: 0.85rem !important;
      font-weight: bold !important;
      box-shadow: 0 6px 24px rgba(255,215,0,0.5) !important;
      animation: ylcPulse 1.4s ease-in-out infinite;
    }
  `;
  document.head.appendChild(s);
}
